/**
 * TrainingCertificate — Printable competence certificate.
 *
 * Displays certificate details and provides a "Print Certificate" button.
 * Uses @media print CSS to hide navigation and show only the certificate.
 * Zero new dependencies.
 *
 * Route: /qms/training/certificate/:certificateId
 * API: GET /@qms/training/certificate?certificate_id=
 *
 * Ref: CR-2026-027
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Container,
  Loader,
  Message,
  Button,
  Icon,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../../api';

export default function TrainingCertificate() {
  const { certificateId } = useParams();
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchApi(
      `${getApiPath()}/@qms/training/certificate?certificate_id=${encodeURIComponent(certificateId)}`,
    )
      .then((json) => {
        setCert(json);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [certificateId]);

  if (loading) {
    return (
      <Container style={{ paddingTop: '3em' }}>
        <Loader active inline="centered" content="Loading certificate..." />
      </Container>
    );
  }

  if (error || !cert) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Message negative content={error || 'Certificate not found'} />
        <Link to="/qms/training">&larr; Back to Training</Link>
      </Container>
    );
  }

  const issuedDate = new Date(cert.issued_at).toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <Container style={{ paddingTop: '2em', paddingBottom: '3em' }}>
      <Helmet title={`Certificate ${cert.certificate_id}`} />

      {/* Print-only CSS */}
      <style>{`
        @media print {
          /* Hide everything except the certificate */
          nav, header, footer, .navigation, .toolbar,
          .header-wrapper, .footer-wrapper, .no-print,
          .breadcrumbs, .slot-container { display: none !important; }

          body { margin: 0; padding: 0; background: white; }

          .certificate-container {
            margin: 0 !important;
            padding: 0 !important;
            max-width: none !important;
          }

          .certificate-card {
            border: 4px double #2c5aa0 !important;
            box-shadow: none !important;
            page-break-inside: avoid;
            min-height: 90vh;
          }
        }
      `}</style>

      <div className="no-print" style={{ marginBottom: '1.5em' }}>
        <Link to="/qms/training">&larr; Back to Training</Link>
        <Button
          color="blue"
          floated="right"
          onClick={() => window.print()}
        >
          <Icon name="print" /> Print Certificate
        </Button>
      </div>

      <div className="certificate-container">
        <div
          className="certificate-card"
          style={{
            border: '4px double #2c5aa0',
            borderRadius: '8px',
            padding: '3em',
            textAlign: 'center',
            maxWidth: '800px',
            margin: '0 auto',
            background: '#fff',
          }}
        >
          {/* Organization */}
          <div style={{ fontSize: '1.1em', color: '#666', marginBottom: '0.5em' }}>
            Ljusa Tider Quality Management System
          </div>

          {/* Title */}
          <h1
            style={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: '2.5em',
              color: '#2c5aa0',
              marginBottom: '0.3em',
              fontWeight: 'normal',
              letterSpacing: '0.05em',
            }}
          >
            CERTIFICATE OF COMPETENCE
          </h1>

          <div
            style={{
              width: '200px',
              height: '2px',
              background: '#2c5aa0',
              margin: '0.5em auto 2em',
            }}
          />

          {/* Awarded to */}
          <div style={{ fontSize: '1.1em', color: '#666', marginBottom: '0.5em' }}>
            This is to certify that
          </div>
          <h2
            style={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: '2em',
              color: '#333',
              margin: '0.3em 0 0.8em',
            }}
          >
            {cert.user_fullname || cert.user_id}
          </h2>

          {/* Module */}
          <div style={{ fontSize: '1.1em', color: '#666', marginBottom: '0.3em' }}>
            has demonstrated competence in
          </div>
          <h3
            style={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: '1.5em',
              color: '#2c5aa0',
              margin: '0.3em 0 0.5em',
            }}
          >
            {cert.module_title}
          </h3>
          <div style={{ fontSize: '0.95em', color: '#888', marginBottom: '2em' }}>
            Per {cert.sop_reference}
          </div>

          {/* Qualified for */}
          <div style={{ fontSize: '1em', color: '#666', marginBottom: '0.3em' }}>
            and is hereby qualified for the following roles:
          </div>
          <div style={{ fontSize: '1.2em', fontWeight: 'bold', color: '#333', marginBottom: '2em' }}>
            {(cert.qualified_for || [])
              .map((r) => r.charAt(0).toUpperCase() + r.slice(1))
              .join(', ')}
          </div>

          <div
            style={{
              width: '200px',
              height: '1px',
              background: '#ccc',
              margin: '2em auto 1.5em',
            }}
          />

          {/* Details */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              maxWidth: '500px',
              margin: '0 auto',
              fontSize: '0.9em',
              color: '#666',
            }}
          >
            <div>
              <div style={{ fontWeight: 'bold' }}>Date Issued</div>
              <div>{issuedDate}</div>
            </div>
            <div>
              <div style={{ fontWeight: 'bold' }}>Certificate ID</div>
              <div>{cert.certificate_id}</div>
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
}
